import { OverlayTrigger, Tooltip } from "react-bootstrap";
import pushHistory from "../../functions/pushHistory";
import React, { useEffect, useState } from "react";

interface ActionButtonProps {
	href?: string;
	history?: any;
	newWindow?: boolean;
	tooltip?: string | null;
	iconType?: string;
	icon?: string;
	show?: boolean;
	onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | MouseEvent) => void;
	colour?: string;
	hoverColour?: string;
	pressed?: boolean;
	disabled?: boolean;
	toolTipPlacement?: any;
	rotateMode?: number;
	stateOverrides?: any;
	hideAfter?: number;
	simulatedClick?: null | { setState: React.Dispatch<React.SetStateAction<boolean>>, constant: boolean };
}

const ActionButton: React.FC<ActionButtonProps> = ({
	href = "",
	history = null,
	newWindow = false,
	tooltip = null,
	iconType = "material-icons",
	icon = "info",
	show = true,
	onClick = (e) => history && pushHistory(history, e, newWindow),
	colour = "rgb(255, 255, 255)",
	hoverColour = "rgb(240, 208, 0)",
	pressed = false,
	disabled = false,
	toolTipPlacement = "top",
	rotateMode = 0,
	stateOverrides = {},
	hideAfter = 0,
	simulatedClick = null,
}) => {
	const [hover, setHover] = useState(false);
	const [rotationState, setRotationState] = useState(0);
	const [clicked, setClicked] = useState(false);
	const [hide, setHide] = useState(true);

	const rotation = stateOverrides.rotation || rotationState;
	const setRotation = stateOverrides.setRotation || setRotationState;

	const currentColour = hover || pressed ? hoverColour : colour;
	const currentColourRaw = currentColour.replace("rgb(", "").replace(")", "");

	const opacityTransition =
		(disabled && show) || (!disabled && show)
			? ""
			: `all ${hideAfter}ms ease-in-out, `;
	const styles = {
		color: `${currentColour}`,
		borderColor: `${currentColour}`,
		backgroundColor: `rgba(${currentColourRaw}, 0.15)`,
		opacity: hideAfter > 0 && !show ? "0" : disabled ? "0.6" : "1",
		transition: `${opacityTransition}color 0.4s ease-in-out, border-color 0.4s ease-in-out, background-color 0.4s ease-in-out`,
		display: hide && !show ? "none" : "inline-block",
	};
	const iconStyles = {
		transform: `rotate(${rotation}deg) scale(${hover ? 1.33 : 1})`,
		transformOrigin: "center center",
		display: "inline-block",
		PointerEvents: "none",
		transition: "transform 0.4s ease-in-out",
	};

	const classNames = `${iconType} notranslate ${show ? "clickable" : ""} unselectable action-button ${disabled || !show ? "disabled" : ""}`;

	const handleClick = (
		e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
	) => {
		if (!disabled && show) {
			onClick(e);
			setClicked(true);
		}
	};

	useEffect(() => {
		if (simulatedClick?.constant === true && show && !disabled) {
			setClicked(true);
			onClick(new MouseEvent("click", { bubbles: true }));
		}	

		if (simulatedClick)
		simulatedClick.setState(false);


	}, [simulatedClick, onClick, show, disabled]);

	useEffect(() => {
		if (hideAfter > 0 && show) {
			setHide(false);
		}

		if (!show) {
			setTimeout(() => setHide(true), hideAfter);
		}
	}, [hideAfter, show]);

	useEffect(() => {
		if (rotateMode !== 0 && clicked) {
			const rotate = Math.abs(rotateMode);
			const multiplier = rotate / rotateMode;
			// always 90 degrees
			if (rotate === 1) setRotation((prev) => prev + 90 * multiplier);
			// rotate forward, then back
			if (rotate === 2)
				setRotation((prev) =>
					prev !== 0 ? 0 : prev + 180 * multiplier,
				);
			// rotate fully
			if (rotate === 3) setRotation((prev) => prev + 360 * multiplier);
			// wiggle
			if (rotate === 4) {
				setRotation((prev) => prev + 40 * multiplier);
				setTimeout(
					() => setRotation((prev) => prev - 90 * multiplier),
					400,
				);
				setTimeout(
					() => setRotation((prev) => prev + 50 * multiplier),
					800,
				);
			}
		}
		setClicked(false);
	}, [clicked, rotateMode, setRotation]);

	useEffect(() => {
		if (disabled) {
			setHover(false);
		}
	}, [disabled]);

	useEffect(() => {
		if (!pressed) {
			setRotation(0);
			setHover(false);
		}
	}, [pressed, setRotation]);

	const anchorEl = (
		<a
			{...(href !== "" && { href: href })}
			onClick={handleClick}
			style={styles}
			onMouseEnter={() => !disabled && setHover(true)}
			onMouseLeave={() => !disabled && setHover(false)}
			className={classNames}>
			<span style={iconStyles}>{icon}</span>
		</a>
	);

	if (tooltip)
		return (
			<OverlayTrigger
				placement={toolTipPlacement}
				overlay={
					<Tooltip id="actionbutton-tooltip">
						<div>{tooltip}</div>
					</Tooltip>
				}>
				{anchorEl}
			</OverlayTrigger>
		);

	return anchorEl;
};

export default ActionButton;
